<template>
  <ModalForm
    v-model="formData"
    :title="title"
    text-btn-confirm="Продолжить"
    :btn-size="hasSetReserve && stage === 'two' ? '100' : 'xl'"
    :only-btn-submit="!(hasSetReserve && stage === 'two')"
    :class="{ 'modal--md': hasSetReserve && stage === 'two' }"
    @request="openWeightDirtyModal"
    @reject="onReject"
  >
    <div v-if="stage === 'one'">
      <RadioGroup name="type" class="mb-30" :options="types" />

      <VSelect
        v-if="stageList.length"
        label="Отправить на этап"
        class="mb-30"
        name="status"
        :options="stageList"
      />

      <VInput
        v-if="formData.type == 'split_consignment'"
        label="Укажите кол-во штук:"
        name="split_qty"
        class="mb-30"
        required
        :pattern="pattern.num"
        :max-value="consignment.value - 1"
        :min-value="1"
        @changeValue="calcWeight"
      >
        <Validation for="required">Поле обязательно</Validation>
        <Validation for="pattern" key-text="num"/>
        <Validation for="maxValue">Кол-во не может быть больше {{ consignment.value - 1 }}</Validation>
        <Validation for="minValue">Кол-во не может быть меньше 1</Validation>
      </VInput>

      <VInput
        v-if="formData.type == 'split_consignment'"
        label="Чистый вес изделий:"
        name="split_weight"
        class="mb-30"
        :pattern="pattern.num100"
        @changeValue="calcQty"
      >
        <Validation for="pattern" key-text="num100"/>
      </VInput>
    </div>

    <!--  Если есть резерв и второй этап, выводим форму заполнения общего веса по метражу  -->
    <div v-else-if="hasSetReserve && stage === 'two'">
      <ConsignmentFormWeightDirty
        v-model="formData.reservedRows"
        class="mb-20"
        :position="position"
        :consignment="consignment"
        :qty="typeIsSplit ? formData.split_qty : consignment.value"
      />
    </div>
    <div v-else-if="stage === 'two'">
      <VInput
        label="Масса:"
        name="weight_dirty"
        class="mb-30"
        required
        :pattern="/^[0-9]\d*[.,]?(\d{0,3}?)$/"
        :custom-validation="formData.type === 'full_consignment' ? validationDirtyWeightMinValue : validationSplitDirtyWeightMinValue"
      >
        <Validation for="required">Поле обязательно</Validation>
        <Validation for="pattern">Только цифры с точностью до тысячных</Validation>
        <Validation for="customValidation">Общий вес не может быть меньше {{formData.type === 'full_consignment' ? consignment.weight_clear : formData.split_weight }}</Validation>
      </VInput>
    </div>
    <div v-else-if="stage === 'three'">
      <VSelect
        label="Материал"
        name="material"
        class="mb-30"
        :options="getMaterialToSpecifySupplier"
        required
      >
        <Validation for="required"/>
      </VSelect>
    </div>
  </ModalForm>
</template>

<script>
import VInput from '@/components/Form/Vinput/VInput.vue'
import ModalForm from '@/components/ui/Modal/ModalForm.vue'
import Validation from '@/components/Form/Validation.vue'

import { mixinModal } from '@/mixins/modal/mixinModal'
import { PATTERN_LIST } from '@/const/validation'
import RadioGroup from '@/components/Form/Radio/RadioGroup.vue'
import { showAlertWarning } from '@/utils/store/alert'
import VSelect from '@/components/Form/VSelect.vue'
import ConsignmentController from '@/controllers/production/Consignment/ConsignmentController'
import { fixedNumberCopy } from '@/utils/number'
import { mapActions, mapGetters } from 'vuex'
import { mixinConsignmentValidationDirtyWeightMinValue } from '@/mixins/form/validation/consignment/mixinConsignmentValidationDirtyWeightMinValue'
import { toNum } from '@/utils/string'
import { transformConsignmentWeightValue } from '@/utils/pages/Production/consignment/transform'
import ConsignmentFormWeightDirty from '@/components/production/consignment/ConsignmentFormWeightDirty.vue'
import { mappedDirtyWeights, mappedParamsWeightWithLength, mappedReserveData } from '@/utils/production/consignment'
import { prepareFormDataPosition } from '@/utils/production/position'
import PositionController from '@/controllers/production/Position/PositionController'

export default {
  name: 'ModalConsignmentSplit',
  components: {
    ConsignmentFormWeightDirty,
    VSelect,
    RadioGroup,
    Validation,
    ModalForm,
    VInput
  },
  mixins: [
    mixinModal,
    mixinConsignmentValidationDirtyWeightMinValue
  ],
  props: {
    consignment: {
      type: Object,
      required: true
    },
    position: {
      type: Object,
      default: () => {}
    },
    isFirstStage: {
      type: Boolean,
      default: false
    },
    stageLine: {
      type: Array,
      default: () => []
    },
    reserveData: {
      type: Array,
      default: null
    },
    isOrderDetail: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      formData: {
        type: '',
        weight_dirty: '',
        split_qty: '',
        split_weight: '',
        status: '',
        reservedRows: this.consignment?.dirty_weights?.length
          ? mappedDirtyWeights(this.consignment.dirty_weights)
          : mappedReserveData(this.reserveData),
        material: null
      },
      pattern: PATTERN_LIST,
      firstModal: true,
      types: [
        {
          title: 'Передать частично',
          value: 'split_consignment',
          disabled: +this.consignment.value === 1
        },
        {
          title: 'Передать всю партию ' + this.consignment.value + ' шт.',
          value: 'full_consignment'
        }
      ],
      stage: 'one',
      responseData: null
    }
  },

  computed: {
    ...mapGetters({
      currentUserId: 'currentUserId'
    }),
    ...mapGetters('permission', {
      permissionUpdateConsignmentWeight: 'permissionUpdateConsignmentWeight'
    }),

    getTitleModalWeight () {
      let title = 'Укажите общий вес потраченного металла '
      if (this.typeIsSplit) {
        title += 'на ' + this.formData.split_qty + ' шт.'
      }

      return title + ' в КГ'
    },

    stageList () {
      if (!this.stageLine?.length) return []

      const indexCurrentStage = this.stageLine.findIndex(stage => stage.status.id === this.consignment.status.id)

      return this.stageLine
        .slice(indexCurrentStage + 1, -3)
        .map(stage => ({
          id: stage.status.id,
          name: stage.status.name
        }))
    },

    title () {
      if (this.stage === 'one') {
        return 'Передать партию'
      } else if (this.hasSetReserve && this.stage === 'two') {
        return 'Укажите общий вес'
      } else if (this.stage === 'three') {
        return 'Укажите поставщика'
      } else {
        return this.getTitleModalWeight
      }
    },

    // Проверка заполнен ли резерв
    hasReserve () {
      return !!this.reserveData.length
    },

    // Проверка нужно ли в данный момент заполнять резерв
    // Если без навивки или из задела, то не делаем резерв
    // Для переноса на след. этап или при делении заполняем только для первого этапа.
    hasSetReserve () {
      return this.hasReserve
        && this.permissionUpdateConsignmentWeight
        && !this.consignment.is_without_winding
        && !this.consignment.warehouse
        && (this.isFirstStage && !this.consignment?.dirty_weights?.length)
    },

    // Проверка, что выбранный тип соответствует дроблению
    typeIsSplit () {
      return this.formData?.type === 'split_consignment'
    },

    // Проверяем нужно ли выводить окно указания поставщика
    hasSetSupplier () {
      return this.position?.material?.supplier_required
        && this.isFirstStage
        && !this.consignment.is_without_winding
        && !this.consignment.warehouse
    },

    // Проверяем нужно ли заполнять общий вес
    hasSetWeightDirty () {
      return this.isFirstStage
        && !this.hasReserve
        && this.permissionUpdateConsignmentWeight
        && !this.hasDirtyWeight()
        && !this.formData.weight_dirty
    },

    // Получение связанных материалов
    getMaterialToSpecifySupplier () {
      const children = this.position?.material?.children || []
      const material = this.position?.material

      return [
        ...children,
        ...(material ? [material] : [])
      ]
    }
  },

  methods: {
    ...mapActions('orderDetail', [
      'updatePositionCurrent'
    ]),

    async stageNextHandler () {
      if (this.typeIsSplit) {
        await this.split()
      } else {
        await this.stageNext()
      }
    },

    async openWeightDirtyModal (data) {
      if (!data.type) {
        showAlertWarning('Пожалуйста, выберите способ передачи партии')
        return
      }

      // При добавлении резерва. Если первый этап,
      // открываем модалку на заполнение веса, иначе отправляем запросы
      if (this.hasSetReserve && this.stage === 'one') {
        this.stage = 'two'
        return
      }

      if (this.hasSetWeightDirty && this.stage === 'one') {
        this.stage = 'two'
        this.calcWeight(this.formData?.split_qty || null)
        return
      }

      if (this.hasSetSupplier) {
        if (this.stage !== 'three') this.stage = 'three'
        else this.setSupplier()
        return
      }

      await this.stageNextHandler()
    },
    hasDirtyWeight () {
      if (this.consignment.warehouse) return true
      if (this.consignment.is_without_winding) return true
      return !!this.consignment?.weight_dirty
    },

    async stageNext () {
      try {
        this.$store.commit('SET_IS_SENDING_REQUEST_MODAL')
        const data = {
          changer_id: this.$store.getters.currentUserId,
          status_id: this.formData?.status?.id || null,
          weight_dirty: this.formData?.weight_dirty ? transformConsignmentWeightValue(this.formData.weight_dirty) : null
        }

        const response = await ConsignmentController.goStageNext(this.consignment.id, data)
        if (response?.status !== 200) return

        if (this.hasSetReserve) {
          this.responseData = response.data
          await this.weightDirtyWithLength()
        } else this.$emit('goStageNext', response.data)
      } finally {
        this.$store.commit('SET_IS_SENDING_REQUEST_MODAL', false)
      }
    },

    async split () {
      try {
        this.$store.commit('SET_IS_SENDING_REQUEST_MODAL')
        const data = {
          value: this.formData?.split_qty || null,
          weight_dirty: this.formData?.weight_dirty || null,
          status_id: this.formData?.status?.id || null
        }
        data.weight_dirty = transformConsignmentWeightValue(data.weight_dirty)

        const response = await ConsignmentController.goSplit(this.consignment.id, data)
        if (response?.status !== 200) return

        response.data.current_consignment = this.consignment.id

        if (this.hasSetReserve) {
          this.responseData = response.data
          await this.weightDirtyWithLength()
        } else this.$emit('goSplit', response.data)
      } finally {
        this.$store.commit('SET_IS_SENDING_REQUEST_MODAL', false)
      }
    },

    /*
    * Установка общего веса, если есть метраж
    * */
    async weightDirtyWithLength () {
      try {
        this.$store.commit('SET_IS_SENDING_REQUEST_MODAL')

        const params = mappedParamsWeightWithLength(this.formData.reservedRows, !this.typeIsSplit)

        const consignmentId = this.responseData.id
        const response = await ConsignmentController.setWeightDirtyWithLength(consignmentId, params)
        if (response?.status !== 200) return

        const weightDirtyData = {
          data: response.data,
          id: consignmentId
        }

        if (this.typeIsSplit) this.$emit('goSplit', this.responseData, weightDirtyData)
        else this.$emit('goStageNext', this.responseData, weightDirtyData)
      } finally {
        this.$store.commit('SET_IS_SENDING_REQUEST_MODAL', false)
      }
    },

    // Указываем поставщика, если необходимо
    async setSupplier () {
      try {
        this.$store.commit('SET_IS_SENDING_REQUEST_MODAL')

        if (this.position.material_id !== this.formData.material?.id) {
          const params = {
            ...this.position,
            ...prepareFormDataPosition(this.position),
            material: this.formData.material,
            material_id: this.formData.material?.id
          }
          if (this.isOrderDetail) await this.updatePositionCurrent(params)
          else {
            params.changer_id = this.currentUserId
            const response = await PositionController.update(params.id, params)
            if (response.status !== 200) return
          }
        }

        await this.stageNextHandler()
      } finally {
        this.$store.commit('SET_IS_SENDING_REQUEST_MODAL', false)
      }
    },

    onReject () {
      this.$emit('reject')
    },

    calcQty (weight) {
      weight = toNum(weight)
      if (!weight) {
        this.formData.split_qty = ''
        return
      }

      const qty = this.consignment.value * weight / this.consignment.weight_clear
      this.formData.split_qty = qty.toFixed()
    },

    calcWeight (qty) {
      qty = toNum(qty)
      if (!qty) {
        this.formData.split_weight = ''
        return
      }

      const weight = qty * this.consignment.weight_clear / this.consignment.value

      this.formData.split_weight = fixedNumberCopy(weight, 2)
    }
  }
}
</script>
